import React from 'react';

import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {useTranslation} from 'common/i18n/hooks';
import {withStyles} from 'common/util/react';
import {react2panel} from 'common/util/react2panel';

import {RecordingEventMetadata, RecordingMetadata, RecordingPageViewEventMetadata} from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import {
  getDisplayName,
  getEventCount,
  getFormattedDuration,
  getReplayEventCount,
  getSearchHighlightPieces,
} from './util';

type ReplayPlaylistRowProps = {
  sortedReplays: RecordingMetadata[];
  currentRecordingIdx: number;
  currentlyPlaying: boolean;
  hasBeenPlayed: boolean;
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  replayPageViewEventsLoaded: boolean;
  replayPageViewEvents: RecordingPageViewEventMetadata[];
  displayName: string;
  displayDate: string;
  displayTime: string;
  searchFilter: string;
  isExpanded: boolean;
  onToggleExpand: (replayId: string) => void;
  onTogglePlay: (replayId: string) => void;
  onHover: (hovered: boolean, currentTarget?: HTMLDivElement) => void;
};

function replayPlaylistRow(props: ReplayPlaylistRowProps, ref: React.Ref<HTMLDivElement>) {
  const rowRef = React.useRef<HTMLDivElement>(null);
  const {t, print} = useTranslation();

  function maybeShowSearchHighlight(inputText: string) {
    const pieces = getSearchHighlightPieces(inputText, props.searchFilter);

    return (
      <>
        {pieces.map((piece, i) => {
          return (
            <span className={piece.highlight ? styles.replaysTextHighlight : ``} key={i}>
              {piece.text}
            </span>
          );
        })}
      </>
    );
  }

  const eventCount = getReplayEventCount(props.replayEvents);
  const eventsLoading = props.replayEvents === undefined;
  const isReplaySelected = props.replay.replayId === props.sortedReplays[props.currentRecordingIdx]?.replayId;
  const getIconToShow = () => {
    if (isReplaySelected) {
      if (props.currentlyPlaying) {
        return `pause`;
      }
      return `play`;
    }
    if (props.hasBeenPlayed) {
      return `restart`;
    }
    return `play`;
  };

  return (
    <div
      className={`${styles.replaysListItem} ${isReplaySelected ? styles.replaysListItemSelected : ``}`}
      ref={rowRef}
      onMouseOver={() => props.onHover(true, rowRef?.current as HTMLDivElement)}
      onMouseOut={() => props.onHover(false)}
    >
      <div className={styles.replaysListItemInfo} ref={ref}>
        <div
          className={`${styles.replaysListItemIconContainer} ${isReplaySelected ? styles.replaysListItemIconContainerActive : styles.replaysListItemIconContainerInactive}`}
          onClick={() => props.onTogglePlay(props.replay.replayId)}
        >
          <SvgIconReact className={styles.replaysListItemIcon} icon={getIconToShow()} role='img' />
        </div>
        <div className={styles.replaysListItemText}>
          <div className={styles.replaysListItemTextTitle}>{maybeShowSearchHighlight(props.displayDate)}</div>
          <div className={styles.replaysListItemTextSubtitle}>
            {maybeShowSearchHighlight(props.displayTime)}
            {` `}• {print(getEventCount(eventCount, eventsLoading))} •{` `}
            {print(getFormattedDuration(props.replay.duration))}
          </div>
        </div>
      </div>
      <div className={styles.replaysListItemIndicators}>
        <ButtonReact
          theme={ButtonTheme.Tertiary}
          icon={props.isExpanded ? `caret-up` : `caret-down`}
          onClick={(ev) => {
            props.onToggleExpand(props.replay.replayId);
            ev.stopPropagation();
          }}
          tooltip={{
            text: props.isExpanded ? t(`session-replay.collapse-replay`) : t(`session-replay.expand-replay`),
          }}
        />
      </div>
    </div>
  );
}

const ReplayPlaylistRowStyled = withStyles(React.forwardRef(replayPlaylistRow), css);
export const ReplayPlaylistRowEl = react2panel(`mp-replay-playlist-row`, ReplayPlaylistRowStyled);

export function DummyPlaceHolderReplayPlaylistRow() {
  const someReplay = {
    replayId: `************`,
    time: 0,
    distinctId: `************`,
    name: `************`,
    avatarUrl: `************`,
    email: `************`,
    duration: 0,
    replayEnv: `************`,
    browser: `************`,
    source: `************`,
    os: `************`,
    startingURL: `************`,
  };
  return (
    <ReplayPlaylistRowStyled
      sortedReplays={[someReplay]}
      currentRecordingIdx={0}
      currentlyPlaying={false}
      hasBeenPlayed={false}
      replay={someReplay}
      replayEvents={[{eventName: `************`, eventTime: 0, quantity: 10}]}
      replayPageViewEventsLoaded={false}
      replayPageViewEvents={[{currentURL: `************`, eventTime: 0}]}
      displayName={getDisplayName(someReplay?.email, someReplay?.name, someReplay?.distinctId)}
      displayDate={``}
      displayTime={``}
      searchFilter={``}
      isExpanded={false}
      onToggleExpand={function (): void {}}
      onTogglePlay={function (): void {}}
      onHover={function (): void {}}
    />
  );
}
